import React, { useState, useEffect, useReducer, useCallback } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import {
  Button,
  IconButton,
  makeStyles,
  Paper,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
  Box,
  CircularProgress,
  Chip,
  Grid
} from "@material-ui/core";

import {
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Receipt as ReceiptIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon,
  Visibility as VisibilityIcon
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../../components/ConfirmationModal";
import PedidoModal from "../../components/PedidoModal";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  searchContainer: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  pedidoCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    cursor: "pointer",
    transition: "all 0.3s",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      transform: "translateY(-2px)",
      boxShadow: theme.shadows[4],
    },
  },
  pedidoHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  pedidoInfo: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
  },
  infoRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  actions: {
    display: "flex",
    gap: theme.spacing(1),
  },
  loadMoreButton: {
    marginTop: theme.spacing(2),
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_PEDIDOS") {
    const pedidos = action.payload;
    const newPedidos = [];

    pedidos.forEach((pedido) => {
      const pedidoIndex = state.findIndex((p) => p.id === pedido.id);
      if (pedidoIndex !== -1) {
        state[pedidoIndex] = pedido;
      } else {
        newPedidos.push(pedido);
      }
    });

    return [...state, ...newPedidos];
  }

  if (action.type === "UPDATE_PEDIDO") {
    const pedido = action.payload;
    const pedidoIndex = state.findIndex((p) => p.id === pedido.id);

    if (pedidoIndex !== -1) {
      state[pedidoIndex] = pedido;
      return [...state];
    } else {
      return [pedido, ...state];
    }
  }

  if (action.type === "DELETE_PEDIDO") {
    const pedidoId = action.payload;
    const pedidoIndex = state.findIndex((p) => p.id === pedidoId);
    if (pedidoIndex !== -1) {
      state.splice(pedidoIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const getStatusColor = (status) => {
  const colors = {
    pendente: "default",
    confirmado: "primary",
    em_producao: "secondary",
    enviado: "#9c27b0",
    entregue: "#4caf50",
    cancelado: "#f44336"
  };
  return colors[status] || "default";
};

const PEDIDO_STATUSES = ["pendente", "confirmado", "em_producao", "enviado", "entregue", "cancelado"];

const getStatusLabel = (status) => {
  return PEDIDO_STATUSES.includes(status) ? i18n.t(`pedidos.status.${status}`) : status;
};

const Pedidos = () => {
  const classes = useStyles();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [pedidos, dispatch] = useReducer(reducer, []);
  const [selectedPedido, setSelectedPedido] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pedidoModalOpen, setPedidoModalOpen] = useState(false);

  const fetchPedidos = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/pedidos", {
        params: { searchParam, pageNumber },
      });
      
      dispatch({ type: "LOAD_PEDIDOS", payload: data.pedidos });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  }, [searchParam, pageNumber]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    fetchPedidos();
  }, [fetchPedidos]);

  const handleOpenPedidoModal = () => {
    setSelectedPedido(null);
    setPedidoModalOpen(true);
  };

  const handleClosePedidoModal = () => {
    setSelectedPedido(null);
    setPedidoModalOpen(false);
  };

  const handleEditPedido = (pedido) => {
    setSelectedPedido(pedido);
    setPedidoModalOpen(true);
  };

  const handleDeletePedido = async (pedidoId) => {
    try {
      await api.delete(`/pedidos/${pedidoId}`);
      toast.success(i18n.t("pedidos.toasts.deleted"));
      dispatch({ type: "DELETE_PEDIDO", payload: pedidoId });
    } catch (err) {
      toastError(err);
    }
    setSelectedPedido(null);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString('pt-BR');
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={i18n.t("pedidos.confirmationModal.deleteTitle")}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeletePedido(selectedPedido.id)}
      >
        {i18n.t("pedidos.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      
      <PedidoModal
        open={pedidoModalOpen}
        onClose={handleClosePedidoModal}
        pedidoId={selectedPedido?.id}
        onSave={(pedido) => {
          if (selectedPedido) {
            dispatch({ type: "UPDATE_PEDIDO", payload: pedido });
          } else {
            dispatch({ type: "UPDATE_PEDIDO", payload: pedido });
          }
          handleClosePedidoModal();
        }}
      />

      <MainHeader>
        <Title>{i18n.t("pedidos.title")}</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder={i18n.t("pedidos.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenPedidoModal}
            startIcon={<AddIcon />}
          >
            {i18n.t("pedidos.buttons.new")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        {pedidos.length === 0 && !loading ? (
          <Box p={3} textAlign="center">
            <Typography variant="body1" color="textSecondary">
              {i18n.t("pedidos.empty")}
            </Typography>
          </Box>
        ) : (
          <>
            {pedidos.map((pedido) => (
              <Paper key={pedido.id} className={classes.pedidoCard} elevation={2}>
                <div className={classes.pedidoHeader}>
                  <div className={classes.pedidoInfo}>
                    <Typography variant="h6">
                      <ReceiptIcon fontSize="small" style={{ verticalAlign: "middle", marginRight: 8 }} />
                      {pedido.numero_pedido}
                    </Typography>
                    
                    <div className={classes.infoRow}>
                      <PersonIcon fontSize="small" />
                      <Typography variant="body2">
                        {pedido.cliente?.NOMECLI || pedido.cliente?.NOMEFANTASIA}
                      </Typography>
                    </div>

                    <Grid container spacing={2} style={{ marginTop: 8 }}>
                      <Grid item xs={12} sm={4}>
                        <div className={classes.infoRow}>
                          <CalendarIcon fontSize="small" />
                          <Typography variant="body2">
                            {formatDate(pedido.data_pedido)}
                          </Typography>
                        </div>
                      </Grid>
                      
                      <Grid item xs={12} sm={4}>
                        <div className={classes.infoRow}>
                          <MoneyIcon fontSize="small" />
                          <Typography variant="body2">
                            {formatCurrency(pedido.valor_total)}
                          </Typography>
                        </div>
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <Chip
                          label={getStatusLabel(pedido.status)}
                          style={{
                            backgroundColor: getStatusColor(pedido.status),
                            color: "white"
                          }}
                          size="small"
                        />
                      </Grid>
                    </Grid>

                    {pedido.itens && pedido.itens.length > 0 && (
                      <Typography variant="caption" color="textSecondary" style={{ marginTop: 8 }}>
                        {i18n.t("pedidos.itemsCount", { count: pedido.itens.length })}
                      </Typography>
                    )}
                  </div>

                  <div className={classes.actions}>
                    <Tooltip title={i18n.t("pedidos.tooltips.view")}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditPedido(pedido)}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title={i18n.t("common.edit")}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditPedido(pedido)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title={i18n.t("pedidos.tooltips.delete")}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedPedido(pedido);
                          setConfirmModalOpen(true);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
              </Paper>
            ))}
            {loading && <TableRowSkeleton avatar columns={4} />}
          </>
        )}
      </Paper>
    </MainContainer>
  );
};

export default Pedidos;
